import { useEffect, useState } from 'react';
import type { SendToIntegrationContainerProps } from './useSendToIntegrationLogic';
import type { IntegrationData } from '../types';
import { useGetIssuesData } from './useGetIssuesData';
import { useGetAllowedIntegrations } from './useGetAllowedIntegrations';
import { useGetConnectedConnections } from './useGetConnectedConnections';
import { useGetConnectionsTemplates } from './useGetConnectionsTemplates';
import { getIntegrationProductNameFromSlug } from '../utils/getIntegrationProductNameFromSlug';

export const useSendToIntegrationContainerData = (
  sendToIntegrationProps: SendToIntegrationContainerProps
) => {
  const { allowedIntegrations, integrationsLoading, integrationsError } =
    useGetAllowedIntegrations(sendToIntegrationProps);
  const {
    connectedConnections,
    connectedConnectionsLoading,
    connectedConnectionsError
  } = useGetConnectedConnections(sendToIntegrationProps);
  const { templatesByConnections, templatesLoading, templatesError } =
    useGetConnectionsTemplates(sendToIntegrationProps, connectedConnections);

  const {
    issues,
    issuesIDs,
    sentIssues,
    issuesLoading,
    issuesError,
    refetchIssues
  } = useGetIssuesData(sendToIntegrationProps);
  const [integrationData, setIntegrationData] = useState<IntegrationData[]>([]);
  const [bearerToken, setBearerToken] = useState<string>('');

  useEffect(() => {
    if (
      !allowedIntegrations.length ||
      !connectedConnections.length ||
      !Object.keys(templatesByConnections).length ||
      issuesLoading
    ) {
      return;
    }

    const integrationDataList = allowedIntegrations.map(integration => {
      const connectionsByIntegration = connectedConnections.filter(
        connection => connection.product_slug === integration
      );
      const connectionsByIntegrationIds = connectionsByIntegration.map(
        connection => connection.id
      );
      const templates = connectionsByIntegration
        .map(({ id }) => templatesByConnections[id] || [])
        .flat();

      const sentIssueLinks = sentIssues
        .filter(issue =>
          connectionsByIntegrationIds.includes(issue.integration_connection_id)
        )
        .map(issue => issue.integration_url);

      const integrationDataItem: IntegrationData = {
        productSlug: integration,
        isMultipleIssues: issues.length > 1,
        productName: getIntegrationProductNameFromSlug(integration),
        canSendIssue: !!templates.length && !sentIssueLinks.length,
        alreadySentForMultiple: issues.length > 1 && !!sentIssueLinks.length,
        canViewIssue: issues.length <= 1 && !!sentIssueLinks.length,
        sentIssueLinks,
        activeConnections: connectionsByIntegration.map(connection => ({
          id: connection.id,
          baseUrl: connection.base_url || '',
          templates: templatesByConnections[connection.id],
          productSlug: connection.product_slug
        })),
        allConnectionsTemplates: templates
      };
      return integrationDataItem;
    });

    setIntegrationData(integrationDataList);
  }, [
    allowedIntegrations,
    connectedConnections,
    issues,
    sentIssues,
    templatesByConnections,
    issuesIDs,
    sentIssues,
    issuesLoading
  ]);

  useEffect(() => {
    async function loadBearerToken() {
      const token = await sendToIntegrationProps.authFn();
      setBearerToken(token);
    }

    loadBearerToken();
  }, []);

  const loading =
    connectedConnectionsLoading ||
    templatesLoading ||
    issuesLoading ||
    integrationsLoading;

  const error =
    connectedConnectionsError ||
    templatesError ||
    issuesError ||
    integrationsError;

  const sendToIntegrationContainerData = {
    hostProduct: sendToIntegrationProps.hostProduct,
    bearerToken,
    issuesID: issuesIDs,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    issues,
    integrationData
  };

  return {
    sendToIntegrationContainerData,
    loading,
    error,
    refetchIssues
  };
};
